import type {
  InfiniteData,
  UseInfiniteQueryResult,
} from "@tanstack/react-query";

import { ArrowUpDown, Check } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/ui/command";

import type { V2PaginatedResponse } from "@/shared/types/api.v2";
import { useVirtualizedList } from "@/shared/hooks";
import { Skeleton } from "@/shared/ui/skeleton";
import { Spinner } from "@/shared/ui/spinner";
import { useFilterPopupValues, useFilterSearch } from "./filter-popup-hooks";

export interface IFilterPopup<TItem> {
  query: UseInfiniteQueryResult<InfiniteData<V2PaginatedResponse<TItem>>>;
  searchParamLabel: string;

  modifiedData: TItem[];

  label: string;
  open: boolean;
  setOpen: (value: boolean) => void;
  setSearch: (value: string) => void;

  extractValue: (item: TItem) => string;
  renderItem: (item: TItem) => React.ReactNode;
}

export const FilterPopup = <TItem,>({
  label,
  query,
  searchParamLabel,
  open,
  extractValue,
  setSearch,
  renderItem,
  modifiedData,
  setOpen,
}: IFilterPopup<TItem>) => {
  const { value, setValue } = useFilterSearch({
    onSearch: setSearch,
  });
  const { ref } = useVirtualizedList({ ...query });
  const { values, onSelect, reset } = useFilterPopupValues({
    label: searchParamLabel,
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-2 text-thColor">
          {label} <ArrowUpDown size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent dialog={true}>
        <Command filter={() => 1}>
          {values && values.length > 0 && (
            <button
              className="text-selected self-start pb-2"
              onClick={() => {
                reset();
                setOpen(false);
              }}
            >
              Очистить ({values.length})
            </button>
          )}

          <CommandInput
            value={value}
            onValueChange={setValue}
            placeholder="Поиск..."
            className="h-9"
          />

          {values && values.length > 0 && (
            <div className="mb-2 mt-1 self-start">
              <span className="font-light">Выбрано</span>: {values.length}
            </div>
          )}
          <CommandList className="h-[150px] [&>div]:flex [&>div]:flex-col [&>div]:gap-1">
            {modifiedData.length === 0 &&
              !query.isFetchingNextPage &&
              !query.isPending && (
                <CommandEmpty>Ничего не найдено</CommandEmpty>
              )}
            {query.isPending && !query.data
              ? [...Array(5)].map((_, index) => (
                  <Skeleton key={index} className="h-10" />
                ))
              : modifiedData.map((item, index) => {
                  const isSelected = values?.includes(extractValue(item));

                  return (
                    <CommandItem
                      key={index}
                      value={extractValue(item)}
                      onSelect={onSelect}
                      ref={index === modifiedData.length - 1 ? ref : null}
                      className={
                        isSelected
                          ? "bg-selected/20 justify-between shadow-[inset_0_0_0_1px_rgba(0,0,0,0.15)]"
                          : ""
                      }
                    >
                      {renderItem(item)}{" "}
                      {isSelected && (
                        <Check size={12} className="stroke-selected" />
                      )}
                    </CommandItem>
                  );
                })}
            {query.isFetchingNextPage && (
              <CommandItem className="flex justify-center p-2">
                <Spinner className="text-textColor" />
              </CommandItem>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
